/**
 * StrategySyncConfirmDialog
 *
 * Shared confirmation step for incremental strategy <-> onboarding sync.
 * Renders the dry-run plan through StrategySyncPlanView (per-screen diff
 * table, variable / AI-asset / loose-screen sections with resolution
 * controls) for a per-block pull, per-block push, or whole-strategy push,
 * then sends the collected resolutions with the real write.
 *
 * Decision-heavy dialog: dismiss is footer Cancel only (no header X),
 * per ui-components invariant #2.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BaseDialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@modules/ui-components';
import type {
  CliStrategyBlockPullData,
  CliStrategyPushData,
  CliStrategySyncResolutions,
} from '@modules/shared-types';
import {
  isSyncPlanNoOp,
  StrategySyncPlanView,
  type StrategySyncPlanData,
} from './StrategySyncPlanView';

export type StrategySyncMode = 'pull-block' | 'push-block' | 'push-strategy';

export interface StrategySyncConfirmDialogProps {
  open: boolean;
  mode: StrategySyncMode;
  dryRunResult: CliStrategyBlockPullData | CliStrategyPushData | null;
  loading?: boolean;
  error?: string | null;
  onConfirm: (resolutions: CliStrategySyncResolutions) => void;
  onClose: () => void;
}

const MODE_TITLES: Record<StrategySyncMode, string> = {
  'pull-block': 'Pull Block from Onboarding',
  'push-block': 'Push Block to Onboarding',
  'push-strategy': 'Push Strategy to Onboarding',
};

export const StrategySyncConfirmDialog: React.FC<StrategySyncConfirmDialogProps> = ({
  open,
  mode,
  dryRunResult,
  loading = false,
  error = null,
  onConfirm,
  onClose,
}) => {
  const [resolutions, setResolutions] = useState<CliStrategySyncResolutions>({});

  useEffect(() => {
    setResolutions({});
  }, [dryRunResult, open]);

  const handleClose = useCallback(() => {
    if (loading) return;
    onClose();
  }, [loading, onClose]);

  const handleConfirm = useCallback(() => {
    onConfirm(resolutions);
  }, [onConfirm, resolutions]);

  const planData: StrategySyncPlanData | null = useMemo(() => {
    if (!dryRunResult) return null;
    const pull = dryRunResult as CliStrategyBlockPullData;
    const push = dryRunResult as CliStrategyPushData;
    return {
      plan: dryRunResult.plan,
      warnings: dryRunResult.warnings,
      variableDiff: dryRunResult.variableDiff,
      assetDiff: push.assetDiff,
      looseScreens: pull.looseScreens,
    };
  }, [dryRunResult]);

  const isPull = mode === 'pull-block';

  // Pull can only adopt loose screens into the block being pulled.
  const availableBlocks = useMemo(() => {
    if (!isPull || !dryRunResult) return [];
    const blockEntityId = (dryRunResult as CliStrategyBlockPullData).blockEntityId;
    return blockEntityId ? [{ blockEntityId }] : [];
  }, [dryRunResult, isPull]);

  const targetLabel = dryRunResult
    ? `${dryRunResult.funnelId} / ${dryRunResult.versionId}`
    : null;

  const noOp = planData ? isSyncPlanNoOp(planData, resolutions) : false;

  const primaryAction = useMemo(() => ({
    label: loading ? 'Syncing...' : 'Confirm',
    onClick: handleConfirm,
    disabled: loading || !dryRunResult || noOp,
    loading,
    'data-testid': 'strategy-sync-confirm',
  }), [loading, handleConfirm, dryRunResult, noOp]);

  const secondaryAction = useMemo(() => ({
    label: 'Cancel',
    onClick: handleClose,
    disabled: loading,
  }), [handleClose, loading]);

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      mode="modal"
      data-testid="strategy-sync-confirm-dialog"
      aria-labelledby="strategy-sync-confirm-dialog-title"
    >
      <DialogHeader
        title={MODE_TITLES[mode]}
        showCloseButton={false}
      />

      <DialogContent>
        <StrategySyncPlanView
          data={planData}
          direction={isPull ? 'pull' : 'push'}
          loading={loading && !dryRunResult && !error}
          error={error}
          targetLabel={targetLabel}
          showLooseSection={isPull}
          availableBlocks={availableBlocks}
          resolutions={resolutions}
          onResolutionsChange={setResolutions}
        />
      </DialogContent>

      <DialogFooter
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      />
    </BaseDialog>
  );
};

StrategySyncConfirmDialog.displayName = 'StrategySyncConfirmDialog';
